import React from 'react';
import { Mail, MailOpen, Paperclip, Check } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';

function formatDate(dateStr) {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    const oneDay = 86400000;

    if (diff < oneDay && date.getDate() === now.getDate()) {
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    if (diff < 7 * oneDay) {
        return date.toLocaleDateString([], { weekday: 'short' });
    }
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

function getSenderName(from) {
    if (!from) return 'Unknown';
    if (from.emailAddress) {
        return from.emailAddress.name || from.emailAddress.address || 'Unknown';
    }
    return String(from);
}

export default function EmailList({
    emails,
    selectedIds,
    onToggleSelect,
    onEmailClick,
    loading,
}) {
    const { theme } = useTheme();

    if (loading) {
        return (
            <div className="flex flex-col gap-2 p-3">
                {[...Array(5)].map((_, i) => (
                    <div key={i} className={`animate-pulse rounded-lg p-3 ${
                        theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100'
                    }`}>
                        <div className={`h-3 rounded w-1/3 mb-2 ${theme === 'dark' ? 'bg-gray-600' : 'bg-gray-200'}`} />
                        <div className={`h-3 rounded w-2/3 mb-2 ${theme === 'dark' ? 'bg-gray-600' : 'bg-gray-200'}`} />
                        <div className={`h-2 rounded w-full ${theme === 'dark' ? 'bg-gray-600' : 'bg-gray-200'}`} />
                    </div>
                ))}
            </div>
        );
    }

    if (!emails || emails.length === 0) {
        return (
            <div className={`flex items-center justify-center h-40 text-sm font-rasa ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
            }`}>
                No emails found
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-0.5 overflow-y-auto">
            {emails.map((email) => {
                const isSelected = selectedIds.includes(email.id);
                const isRead = email.isRead;
                return (
                    <div
                        key={email.id}
                        className={`group flex items-start gap-2 px-3 py-2.5 cursor-pointer transition-colors ${
                            isSelected
                                ? theme === 'dark' ? 'bg-blue-900/30 border-l-2 border-blue-500' : 'bg-blue-50 border-l-2 border-blue-500'
                                : theme === 'dark' ? 'hover:bg-gray-700/50 border-l-2 border-transparent' : 'hover:bg-gray-50 border-l-2 border-transparent'
                        }`}
                        onClick={() => onEmailClick(email)}
                    >
                        {/* Checkbox */}
                        <button
                            onClick={(e) => { e.stopPropagation(); onToggleSelect(email.id); }}
                            className={`mt-0.5 flex-shrink-0 w-5 h-5 rounded border flex items-center justify-center transition-colors ${
                                isSelected
                                    ? 'bg-blue-600 border-blue-600 text-white'
                                    : theme === 'dark' ? 'border-gray-600 hover:border-gray-400' : 'border-gray-300 hover:border-gray-400'
                            }`}
                        >
                            {isSelected && <Check size={12} />}
                        </button>

                        {/* Email content */}
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2">
                                <span className={`text-base font-rasa truncate ${
                                    !isRead
                                        ? 'font-semibold'
                                        : theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                                }`}>
                                    {getSenderName(email.from)}
                                </span>
                                <span className={`text-sm font-rasa flex-shrink-0 ${
                                    theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
                                }`}>
                                    {formatDate(email.receivedDateTime)}
                                </span>
                            </div>
                            <div className={`text-base font-unna truncate ${
                                !isRead
                                    ? 'font-medium'
                                    : theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                            }`}>
                                {email.subject || '(no subject)'}
                            </div>
                            <div className={`text-sm font-rasa truncate mt-0.5 ${
                                theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
                            }`}>
                                {email.bodyPreview}
                            </div>
                            {email.hasAttachments && (
                                <Paperclip size={12} className={`mt-1 ${
                                    theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
                                }`} />
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
